import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Clock, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost({ onStartTax }) {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/posts/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setError("We couldn't load this article. It may have been moved."));
  }, [slug]);

  if (error) {
    return (
      <div data-testid="blogpost-error" className="pt-32 pb-24 text-center px-4 bg-[#F7F8FA] min-h-screen">
        <p className="text-[#1F2933]">{error}</p>
        <Link to="/tax-tips-blog" data-testid="blogpost-back" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#86680A]">
          <ArrowLeft size={15} /> Back to Tax Tips
        </Link>
      </div>
    );
  }

  if (!post) return <div className="pt-32 pb-24 bg-[#F7F8FA] min-h-screen" data-testid="blogpost-loading" />;

  return (
    <div data-testid="blogpost-page" className="pt-16 bg-[#F7F8FA]">
      <SEO path={`/tax-tips-blog/${post.slug}`} type="article"
        title={`${post.title} | Marizema Tax Service`}
        description={post.excerpt}
        jsonLd={{
          "@context": "https://schema.org", "@type": "BlogPosting",
          "headline": post.title, "description": post.excerpt, "image": post.image, "datePublished": post.date,
          "author": { "@type": "Organization", "name": "Marizema Tax Service LLC" }
        }} />
      <article className="py-14 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/tax-tips-blog" data-testid="blogpost-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#86680A] hover:text-[#0B2545]">
            <ArrowLeft size={15} /> All Tax Tips
          </Link>
          <div className="mt-6 flex items-center gap-3 text-[11px] uppercase tracking-wide">
            <span className="text-[#86680A] font-semibold">{post.category}</span>
            <span className="text-slate-600">{post.date}</span>
            <span className="flex items-center gap-1 text-slate-600"><Clock size={12} /> {post.read_time}</span>
          </div>
          <h1 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0B2545] leading-tight">{post.title}</h1>
          <div className="mt-8 rounded-2xl overflow-hidden">
            <img src={post.image} alt={post.title} className="w-full h-60 sm:h-80 object-cover" />
          </div>
          <div className="mt-10 space-y-5">
            {post.content.map((para, i) => (
              <p key={i} className="text-base text-[#1F2933]/90 leading-relaxed">{para}</p>
            ))}
          </div>
          <div className="mt-12 bg-[#0B2545] border border-[#C9A227]/30 rounded-2xl p-7 text-center">
            <h2 className="font-serif-display text-xl sm:text-2xl font-semibold text-white">Ready for a stress-free tax season?</h2>
            <p className="mt-2 text-sm text-slate-300">Start your tax return online — our Tampa team will be in touch within 24 hours.</p>
            <button data-testid="blogpost-cta-start-tax" onClick={onStartTax}
              className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C9A227] text-[#0B2545] text-sm font-bold hover:bg-[#D4AF37] transition-colors">
              Start Tax Return <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </article>
    </div>
  );
}
